"use client";

import Image from "next/image";
import Link from "next/link";
import { roomsData } from "@/data/chambres";
import ReservationButton from "@/components/buttons/ReservationButton";
import { BannerSection } from "@/components/sections/BannerSection";
import { BreadcrumbHomepage } from "@/components/breadcrumbs/BreadcrumbHomepage";
import { useI18n } from "@/i18n/client";
import { getLocalizedHref } from "@/utils/localization";

export default function ChambresPage() {
  const { t, locale } = useI18n();

  return (
    <section>
      {/* Bannière avec titre */}
      <BannerSection title={t("rooms.title")} />
      <BreadcrumbHomepage className="text-black" />

      {/* Section principale */}
      <div className="w-full px-4 py-20 text-lg ph:px-24 md:px-48 lg:px-72 xl:px-96">
        <div className="flex flex-col gap-16">
          <div className="flex flex-col items-center gap-2">
            <p className="text-center text-xl">{t("rooms.subtitle")}</p>
          </div>

          <div className="flex flex-col gap-4">
            <div className="grid grid-cols-1 gap-10 lg:grid-cols-2 lg:gap-16 xl:gap-20">
              {roomsData.map((room) => (
                <div key={room.id} className="flex flex-col overflow-hidden">
                  {/* Image de la chambre */}
                  <div className="relative h-64 w-full overflow-hidden rounded-2xl lg:h-[24rem] xl:h-[28rem]">
                    <Image
                      src={room.photos[0]}
                      alt={`${locale === "fr" ? "Chambre" : "Room"} ${room.name}`}
                      fill
                      className="object-cover transition-transform duration-700 hover:scale-105"
                    />
                  </div>

                  {/* Informations sur la chambre */}
                  <div className="flex flex-col gap-2 pt-4 lg:pt-6">
                    <h3 className="text-2xl font-light tracking-wide lg:text-3xl">
                      {room.name}
                    </h3>
                    <p className="text-xl font-light lg:text-xl">
                      {t(room.shortDescriptionKey as any)}
                    </p>
                    <div className="mt-1 flex flex-wrap gap-6">
                      <span className="text-lg font-light">
                        {room.nbPerson.min === room.nbPerson.max
                          ? `${room.nbPerson.max} ${room.nbPerson.max > 1 ? t("rooms.persons") : t("rooms.person")}`
                          : `${room.nbPerson.min} ${t("rooms.to")} ${room.nbPerson.max} ${t("rooms.persons")}`}
                      </span>
                      <span className="text-lg font-light">
                        {t("rooms.startingFrom")} {room.prices[0].price}€
                      </span>
                    </div>
                    <p className="mt-2 text-xl font-light">
                      {t(room.longDescriptionKey as any).substring(0, 150)}...
                    </p>
                    <div className="mt-4 flex gap-4">
                      <Link
                        href={getLocalizedHref(`/chambres/${room.id}`, locale)}
                        className="inline-block border-b border-secondary pb-1 text-xl text-secondary transition-all hover:border-black hover:text-black"
                      >
                        {t("rooms.discover")}
                      </Link>
                    </div>
                  </div>
                </div>
              ))}
            </div>

            {/* CTA pour réserver */}
            <div className="mt-12 flex flex-col items-center justify-center gap-4 sm:flex-row">
              <ReservationButton
                variant="primary"
                translationKey="rooms.bookStay"
              />
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
